import {
  ConstructionStage,
  LongLeadEquipmentStatus,
  NoticeToProceedStatus,
} from '@/types/compliance';
import {
  BatteryConfiguration,
  ChargeRestriction,
  ChargingEnergyResponsibility,
  ChargingSource,
  CompanyCreditProfile,
  CompanyRole,
  CreditSupportUnit,
  DispatchRights,
  ISO,
  MeasurementBasis,
  PRODUCT_TYPE,
  PROJECT_OPERATIONAL_STATUS,
  PriceUnit,
  ProductScope,
  RegulatoryStatus,
  SettlementIndex,
  SettlementMethod,
  StorageTechnology,
  TbVariant,
  UserRole,
  VolumeUnit,
} from '@/utils/enums';

import { baseApi } from './baseApi';
import { ApiTags, ReferenceTagIds } from './tags';

export type AvailableUserRole = {
  id: UserRole;
  name: string;
  description: string;
};

export type AvailableUserRoles = AvailableUserRole[];

export type ReferenceUserRole = {
  id: CompanyRole;
  name: string;
  availableRoles: AvailableUserRoles;
};

export type ReferenceUserRoles = ReferenceUserRole[];

export type RegulatoryStatusDto = {
  id: RegulatoryStatus;
  name: string;
  description: string;
};

export type CreditProfileDto = {
  id: CompanyCreditProfile;
  name: string;
  description: string;
};

export type CompanyRoleDto = {
  id: CompanyRole;
  name: string;
  description: string;
};

export type CountryDto = {
  id: number;
  name: string;
  codeIsoAlpha2: string;
  codeIsoAlpha3: string;
};

export type StateDto = {
  id: number;
  countryId: number;
  name: string;
  abbreviation: string;
};

export type ProjectReferenceDto = {
  id: string;
  name: string;
};

/**
 * A product type with the units of its prices and Credit Support, as the backend's product rules
 * catalog gives them. The variable O&M rate and sharing threshold units are `null` when the
 * product does not negotiate that term.
 */
export type ProductTypeDto = {
  id: string;
  name: string;
  requiresSefConsent: boolean;
  priceUnit: PriceUnit;
  variableOmRateUnit: PriceUnit | null;
  sharingThresholdUnit: PriceUnit | null;
  creditSupportUnit: CreditSupportUnit;
};

/** The optional query of `GET /reference/product-types`. */
export type ProductTypesParams = {
  /** Limits the list to the product types the technology can trade. */
  technology?: string;
};

export type SettlementIndexDto = {
  id: string;
  name: string;
  symbol: string;
};

export type HubZoneMatrixDto = {
  id: number;
  iso: ISO;
  settlementPoint: string;
  settlementIndex: SettlementIndex;
};

export type HubZonesParams = {
  iso: ISO | ISO[];
};

export type ContractTypesParams = {
  productType?: PRODUCT_TYPE;
  projectOperationalStatus?: PROJECT_OPERATIONAL_STATUS;
};

export type ContractCreditProfilesParams = {
  companyCreditProfile?: CompanyCreditProfile;
  side?: 'BID';
  /** Limits the list to the profiles the product allows; sent only when set. */
  productType?: PRODUCT_TYPE;
};

export type AdvisorRoleSearchParams = {
  companyRole: CompanyRole;
};

export type CountyDto = {
  id: number;
  stateId: number;
  name: string;
};

export type PanelTypeDto = {
  id: string;
  name: string;
};

export type PanelTechnologyDto = {
  id: string;
  name: string;
};

export type TrackingTypeDto = {
  id: string;
  name: string;
};

export type IsoDto = {
  id: string;
  name: string;
};

export type HubZoneDto = {
  id: number;
  settlementPoint: string;
  isoId: string;
};

export type InterconnectionPointDto = {
  id: number;
  name: string;
  isoId: string;
};

export type InterconnectionVoltageDto = {
  id: string;
  name: string;
};

export type ComplianceStatusDto = {
  id: string;
  name: string;
};

/**
 * An option from an enum reference endpoint (`ProjectReferenceDTO`), with the id
 * narrowed to the backend enum it comes from. `name` is the backend label.
 */
export type EnumReferenceDto<T extends string> = {
  id: T;
  name: string;
};

export type BatteryConfigurationDto = EnumReferenceDto<BatteryConfiguration>;
export type ChargingSourceDto = EnumReferenceDto<ChargingSource>;
export type ChargeRestrictionDto = EnumReferenceDto<ChargeRestriction>;
export type StorageTechnologyDto = EnumReferenceDto<StorageTechnology>;
export type ConstructionStageDto = EnumReferenceDto<ConstructionStage>;
export type NoticeToProceedStatusDto = EnumReferenceDto<NoticeToProceedStatus>;
export type LongLeadEquipmentStatusDto =
  EnumReferenceDto<LongLeadEquipmentStatus>;
export type PriceUnitDto = EnumReferenceDto<PriceUnit>;
export type VolumeUnitDto = EnumReferenceDto<VolumeUnit>;
export type CreditSupportUnitDto = EnumReferenceDto<CreditSupportUnit>;
export type SettlementMethodDto = EnumReferenceDto<SettlementMethod>;
export type TbVariantDto = EnumReferenceDto<TbVariant>;
export type ProductScopeDto = EnumReferenceDto<ProductScope>;
export type DispatchRightsDto = EnumReferenceDto<DispatchRights>;
export type MeasurementBasisDto = EnumReferenceDto<MeasurementBasis>;
export type ChargingEnergyResponsibilityDto =
  EnumReferenceDto<ChargingEnergyResponsibility>;

export type BidStatusDto = {
  id: string;
  name: string;
  description: string;
};

export type MarketStatusDto = {
  serverTime: string;
  nextOpen: string;
  nextClose: string;
  marketTimezone: string;
};

export type TechnologiesDto = {
  id: string;
  name: string;
  description: string;
  isVisible: boolean;
};

export type ContractingFieldDto = {
  fieldKey: string;
  dataType: string;
  description: string;
};

/** An external document whose host protects it with a password. */
export type ProtectedDocument = 'FEE_SCHEDULE' | 'PROTECTED_PAGES';

export type DocumentPasswordDto = {
  document: ProtectedDocument;
  password: string;
};

export type AppVersionDto = {
  version: string;
};

const BASE_URL = '/api/v1/reference';

export const referenceApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getReferenceUserRoles: build.query<ReferenceUserRoles, void>({
      query: () => `${BASE_URL}/user-roles`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.USER_ROLES },
      ],
    }),
    getRegulatoryStatuses: build.query<RegulatoryStatusDto[], void>({
      query: () => `${BASE_URL}/regulatory-statuses`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.REGULATORY_STATUSES },
      ],
    }),
    getCreditProfiles: build.query<CreditProfileDto[], void>({
      query: () => `${BASE_URL}/credit-profiles`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.CREDIT_PROFILES },
      ],
    }),
    getCompanyRoles: build.query<CompanyRoleDto[], void>({
      query: () => `${BASE_URL}/company-roles`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.COMPANY_ROLES },
      ],
    }),
    getCountries: build.query<CountryDto[], void>({
      query: () => `${BASE_URL}/countries`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.COUNTRIES },
      ],
    }),
    getStates: build.query<StateDto[], number>({
      query: countryId => `${BASE_URL}/countries/${countryId}/states`,
      providesTags: (result, error, countryId) => [
        {
          type: ApiTags.REFERENCES,
          id: `${ReferenceTagIds.STATES}-${countryId}`,
        },
      ],
    }),
    getCounties: build.query<CountyDto[], number>({
      query: stateId => `${BASE_URL}/states/${stateId}/counties`,
      providesTags: (result, error, stateId) => [
        {
          type: ApiTags.REFERENCES,
          id: `${ReferenceTagIds.COUNTIES}-${stateId}`,
        },
      ],
    }),
    getAdvisorRolesByCompanyRole: build.query<
      ReferenceUserRole,
      AdvisorRoleSearchParams
    >({
      query: params => {
        const searchParams = new URLSearchParams();
        searchParams.append('companyRole', params.companyRole);
        return `${BASE_URL}/advisor-roles?${searchParams.toString()}`;
      },
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.ADVISOR_ROLES,
        },
      ],
    }),
    getPanelTypes: build.query<PanelTypeDto[], void>({
      query: () => `${BASE_URL}/panel-types`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.PANEL_TYPES },
      ],
    }),
    getTechnologies: build.query<TechnologiesDto[], boolean | void>({
      query: all => ({
        url: `${BASE_URL}/technologies`,
        params: all
          ? {
              all: true,
            }
          : undefined,
      }),
      providesTags: (result, error, panelType) => [
        {
          type: ApiTags.REFERENCES,
          id: `${ReferenceTagIds.TECHNOLOGIES}-${panelType}`,
        },
      ],
    }),
    getPanelTechnologies: build.query<PanelTechnologyDto[], string>({
      query: panelType =>
        `${BASE_URL}/panel-technologies?panelType=${panelType}`,
      providesTags: (result, error, panelType) => [
        {
          type: ApiTags.REFERENCES,
          id: `${ReferenceTagIds.PANEL_TECHNOLOGIES}-${panelType}`,
        },
      ],
    }),
    getTrackingTypes: build.query<TrackingTypeDto[], void>({
      query: () => `${BASE_URL}/tracking-types`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.TRACKING_TYPES },
      ],
    }),
    getOfferStatuses: build.query<ProjectReferenceDto[], void>({
      query: () => `${BASE_URL}/offer-statuses`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.OFFER_STATUSES },
      ],
    }),
    getProductTypes: build.query<ProductTypeDto[], ProductTypesParams | void>({
      // With no technology the URL and tag stay those of the unfiltered list.
      query: params =>
        params?.technology
          ? `${BASE_URL}/product-types?${new URLSearchParams({
              technology: params.technology,
            }).toString()}`
          : `${BASE_URL}/product-types`,
      providesTags: (result, error, params) => [
        {
          type: ApiTags.REFERENCES,
          id: params?.technology
            ? `${ReferenceTagIds.PRODUCT_TYPES}-${params.technology}`
            : ReferenceTagIds.PRODUCT_TYPES,
        },
      ],
    }),
    getContractTypes: build.query<ProjectReferenceDto[], ContractTypesParams>({
      query: ({ productType, projectOperationalStatus }) => {
        const searchParams = new URLSearchParams();

        if (productType) {
          searchParams.append('productType', productType);
        }

        if (projectOperationalStatus) {
          searchParams.append(
            'projectOperationalStatus',
            projectOperationalStatus
          );
        }

        return `${BASE_URL}/contract-types?${searchParams.toString()}`;
      },
      providesTags: (result, error, { productType }) => [
        {
          type: ApiTags.REFERENCES,
          id: `${ReferenceTagIds.CONTRACT_TYPES}-${productType}`,
        },
      ],
    }),
    getContractCreditProfiles: build.query<
      ProjectReferenceDto[],
      ContractCreditProfilesParams
    >({
      query: ({ companyCreditProfile, side, productType }) => {
        const searchParams = new URLSearchParams();

        if (companyCreditProfile) {
          searchParams.append('companyCreditProfile', companyCreditProfile);
        }

        if (side) {
          searchParams.append('side', side);
        }

        if (productType) {
          searchParams.append('productType', productType);
        }

        return `${BASE_URL}/contract-credit-profiles?${searchParams.toString()}`;
      },
      // The product type is appended only when set, so calls without one keep their tag.
      providesTags: (
        result,
        error,
        { companyCreditProfile, side, productType }
      ) => [
        {
          type: ApiTags.REFERENCES,
          id: `${ReferenceTagIds.CONTRACT_CREDIT_PROFILES}-${companyCreditProfile}-${side}${
            productType ? `-${productType}` : ''
          }`,
        },
      ],
    }),
    getNegativePricing: build.query<ProjectReferenceDto[], void>({
      query: () => `${BASE_URL}/negative-pricing`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.NEGATIVE_PRICING },
      ],
    }),
    getApplicableRecPrograms: build.query<ProjectReferenceDto[], void>({
      query: () => `${BASE_URL}/applicable-rec-programs`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.APPLICABLE_REC_PROGRAMS,
        },
      ],
    }),
    getPerformanceGuaranteeMethods: build.query<ProjectReferenceDto[], void>({
      query: () => `${BASE_URL}/performance-guarantee-methods`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.PERFORMANCE_GUARANTEE_METHODS,
        },
      ],
    }),
    getApplicableTrackingSystems: build.query<ProjectReferenceDto[], void>({
      query: () => `${BASE_URL}/applicable-tracking-systems`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.APPLICABLE_TRACKING_SYSTEMS,
        },
      ],
    }),
    getRecDeliveryMethodologies: build.query<ProjectReferenceDto[], void>({
      query: () => `${BASE_URL}/rec-delivery-methodologies`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.REC_DELIVERY_METHODOLOGIES,
        },
      ],
    }),
    getIsos: build.query<ProjectReferenceDto[], void>({
      query: () => `${BASE_URL}/isos`,
      providesTags: [{ type: ApiTags.REFERENCES, id: ReferenceTagIds.ISOS }],
    }),
    getSettlementIndexes: build.query<SettlementIndexDto[], void>({
      query: () => `${BASE_URL}/settlement-indexes`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.SETTLEMENT_INDEXES },
      ],
    }),
    getHubZones: build.query<HubZoneMatrixDto[], HubZonesParams>({
      query: ({ iso }) => {
        const isoArray = Array.isArray(iso) ? iso : [iso];

        const searchParams = new URLSearchParams();

        isoArray.forEach(iso => searchParams.append('iso', iso));

        return `${BASE_URL}/hub-zones?${searchParams.toString()}`;
      },
      providesTags: (result, error, { iso }) => {
        const isoArray = Array.isArray(iso) ? iso : [iso];

        return [
          {
            type: ApiTags.REFERENCES,
            id: `${ReferenceTagIds.HUB_ZONES}-${isoArray.join('-')}`,
          },
        ];
      },
    }),
    getInterconnectionPoints: build.query<InterconnectionPointDto[], string>({
      query: iso => `${BASE_URL}/interconnection-points?iso=${iso}`,
      providesTags: (result, error, iso) => [
        {
          type: ApiTags.REFERENCES,
          id: `${ReferenceTagIds.INTERCONNECTION_POINTS}-${iso}`,
        },
      ],
    }),
    getInterconnectionVoltages: build.query<InterconnectionVoltageDto[], void>({
      query: () => `${BASE_URL}/interconnection-voltages`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.INTERCONNECTION_VOLTAGES,
        },
      ],
    }),
    getCompliancePermitStatuses: build.query<ComplianceStatusDto[], void>({
      query: () => `${BASE_URL}/compliance-permit-statuses`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.COMPLIANCE_PERMIT_STATUSES,
        },
      ],
    }),
    getComplianceDocumentStatuses: build.query<ComplianceStatusDto[], void>({
      query: () => `${BASE_URL}/compliance-document-statuses`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.COMPLIANCE_DOCUMENT_STATUSES,
        },
      ],
    }),
    getBatteryConfigurations: build.query<BatteryConfigurationDto[], void>({
      query: () => `${BASE_URL}/battery-configurations`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.BATTERY_CONFIGURATIONS,
        },
      ],
    }),
    getChargingSources: build.query<ChargingSourceDto[], void>({
      query: () => `${BASE_URL}/charging-sources`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.CHARGING_SOURCES },
      ],
    }),
    getChargeRestrictions: build.query<ChargeRestrictionDto[], void>({
      query: () => `${BASE_URL}/charge-restrictions`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.CHARGE_RESTRICTIONS },
      ],
    }),
    getStorageTechnologies: build.query<StorageTechnologyDto[], void>({
      query: () => `${BASE_URL}/storage-technologies`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.STORAGE_TECHNOLOGIES },
      ],
    }),
    getConstructionStages: build.query<ConstructionStageDto[], void>({
      query: () => `${BASE_URL}/construction-stages`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.CONSTRUCTION_STAGES },
      ],
    }),
    getNoticeToProceedStatuses: build.query<NoticeToProceedStatusDto[], void>({
      query: () => `${BASE_URL}/notice-to-proceed-statuses`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.NOTICE_TO_PROCEED_STATUSES,
        },
      ],
    }),
    getLongLeadEquipmentStatuses: build.query<
      LongLeadEquipmentStatusDto[],
      void
    >({
      query: () => `${BASE_URL}/long-lead-equipment-statuses`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.LONG_LEAD_EQUIPMENT_STATUSES,
        },
      ],
    }),
    getBidStatuses: build.query<BidStatusDto[], void>({
      query: () => `${BASE_URL}/bid-statuses`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.BID_STATUSES },
      ],
    }),
    getPriceUnits: build.query<PriceUnitDto[], void>({
      query: () => `${BASE_URL}/price-units`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.PRICE_UNITS },
      ],
    }),
    getCreditSupportUnits: build.query<CreditSupportUnitDto[], void>({
      query: () => `${BASE_URL}/credit-support-units`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.CREDIT_SUPPORT_UNITS },
      ],
    }),
    getVolumeUnits: build.query<VolumeUnitDto[], void>({
      query: () => `${BASE_URL}/volume-units`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.VOLUME_UNITS },
      ],
    }),
    getSettlementMethods: build.query<SettlementMethodDto[], void>({
      query: () => `${BASE_URL}/settlement-methods`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.SETTLEMENT_METHODS },
      ],
    }),
    getTbVariants: build.query<TbVariantDto[], void>({
      query: () => `${BASE_URL}/tb-variants`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.TB_VARIANTS },
      ],
    }),
    getProductScopes: build.query<ProductScopeDto[], void>({
      query: () => `${BASE_URL}/product-scopes`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.PRODUCT_SCOPES },
      ],
    }),
    getDispatchRights: build.query<DispatchRightsDto[], void>({
      query: () => `${BASE_URL}/dispatch-rights`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.DISPATCH_RIGHTS },
      ],
    }),
    getMeasurementBases: build.query<MeasurementBasisDto[], void>({
      query: () => `${BASE_URL}/measurement-bases`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.MEASUREMENT_BASES },
      ],
    }),
    getChargingEnergyResponsibilities: build.query<
      ChargingEnergyResponsibilityDto[],
      void
    >({
      query: () => `${BASE_URL}/charging-energy-responsibilities`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.CHARGING_ENERGY_RESPONSIBILITIES,
        },
      ],
    }),
    getMarketStatus: build.query<MarketStatusDto, void>({
      query: () => `${BASE_URL}/market/status`,
    }),
    getContractingFields: build.query<ContractingFieldDto[], void>({
      query: () => `${BASE_URL}/contracting-fields`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.CONTRACTING_FIELDS,
        },
      ],
    }),
    getDocumentPasswords: build.query<DocumentPasswordDto[], void>({
      // A failure resolves to no passwords rather than rejecting. Every screen with
      // a Fee Schedule link runs this query, and a rejection would reach the global
      // error snackbar on screens where the user did nothing; the links still work
      // without a password, so the view degrades silently instead.
      queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery(`${BASE_URL}/document-passwords`);
        if (result.error) {
          console.error('Could not load document passwords', result.error);
        }
        return result.error
          ? { data: [] }
          : { data: (result.data ?? []) as DocumentPasswordDto[] };
      },
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.DOCUMENT_PASSWORDS,
        },
      ],
    }),
    getAppVersion: build.query<string | null, void>({
      // A failure resolves to no version rather than rejecting, for the same reason as
      // getDocumentPasswords: the user menu runs this on every page, and the menu simply
      // shows no version line without one.
      queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery(`${BASE_URL}/version`);
        if (result.error) {
          console.error('Could not load the deployed version', result.error);
          return { data: null };
        }
        return {
          data: (result.data as AppVersionDto | undefined)?.version ?? null,
        };
      },
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.APP_VERSION },
      ],
    }),
  }),
});

export const {
  useGetReferenceUserRolesQuery,
  useGetRegulatoryStatusesQuery,
  useGetCreditProfilesQuery,
  useGetCompanyRolesQuery,
  useGetCountriesQuery,
  useGetStatesQuery,
  useGetCountiesQuery,
  useGetAdvisorRolesByCompanyRoleQuery,
  useGetPanelTypesQuery,
  useGetPanelTechnologiesQuery,
  useGetTrackingTypesQuery,
  useGetInterconnectionPointsQuery,
  useGetInterconnectionVoltagesQuery,
  useGetComplianceDocumentStatusesQuery,
  useGetCompliancePermitStatusesQuery,
  useGetBatteryConfigurationsQuery,
  useGetChargingSourcesQuery,
  useGetChargeRestrictionsQuery,
  useGetStorageTechnologiesQuery,
  useGetConstructionStagesQuery,
  useGetNoticeToProceedStatusesQuery,
  useGetLongLeadEquipmentStatusesQuery,
  useGetOfferStatusesQuery,
  useGetProductTypesQuery,
  useGetContractTypesQuery,
  useGetContractCreditProfilesQuery,
  useGetNegativePricingQuery,
  useGetApplicableRecProgramsQuery,
  useGetPerformanceGuaranteeMethodsQuery,
  useGetApplicableTrackingSystemsQuery,
  useGetRecDeliveryMethodologiesQuery,
  useGetIsosQuery,
  useGetSettlementIndexesQuery,
  useGetHubZonesQuery,
  useGetBidStatusesQuery,
  useGetPriceUnitsQuery,
  useGetCreditSupportUnitsQuery,
  useGetVolumeUnitsQuery,
  useGetSettlementMethodsQuery,
  useGetTbVariantsQuery,
  useGetProductScopesQuery,
  useGetDispatchRightsQuery,
  useGetMeasurementBasesQuery,
  useGetChargingEnergyResponsibilitiesQuery,
  useGetTechnologiesQuery,
  useGetMarketStatusQuery,
  useGetContractingFieldsQuery,
  useGetDocumentPasswordsQuery,
  useGetAppVersionQuery,
} = referenceApi;
